// ============================================================================
// Componente: Fiscal.js (MÓDULO FISCAL)
// Objetivo: Casco do módulo Fiscal — mesmo padrão visual/estrutural dos módulos
//           Contabilidade/Financeiro/RH (uma Sidebar + cabeçalho + barra de abas),
//           na paleta ROXA. As ferramentas fiscais (Apuração de Impostos e
//           Auditoria Fiscal) vivem aqui como conteúdo EMBUTIDO (prop `embedded`),
//           reunidas no submódulo de Automações — em vez de páginas soltas no antigo
//           hub de Automações, que foi descontinuado.
// ============================================================================

import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import {
    ArrowRight, ShieldAlert, LayoutDashboard, Zap, BarChart3
} from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';

import FiscalAutomacoes from './automacoes/FiscalAutomacoes';
import FiscalRelatorios from './relatorios/FiscalRelatorios';

// 1. PERMISSÕES POR CARD — mesmos perfis que acessavam as automações fiscais no hub antigo.
const PERMISSIONS = {
    'automacoes': ['Fiscal', 'Admin', 'Gerente Adm', 'Diretoria'],
    'relatorios': ['Fiscal', 'Admin', 'Gerente Adm', 'Diretoria'],
};

// 2. CARDS DO MÓDULO — paleta roxa (Fiscal).
const modulesList = [
    {
        id: 'automacoes',
        label: 'Automações',
        icon: Zap,
        color: '#7c3aed',
        bgColor: '#ede9fe',
        desc: 'Ferramentas fiscais: apuração de impostos (ICMS, DIFAL, PIS/COFINS) e auditoria fiscal (SPED × SAP/Sovos).',
        stat: 'Automações'
    },
    {
        id: 'relatorios',
        label: 'Relatórios',
        icon: BarChart3,
        color: '#6d28d9',
        bgColor: '#ede9fe',
        desc: 'Relatórios prontos para consulta na tela e exportação em Excel. Hoje: Fechamento Mensal (visão Fiscal).',
        stat: 'Relatórios'
    },
];

const FiscalPainel = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = user?.perfil || '';

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Só mostra os cards/abas que o perfil pode acessar.
    const allowedModules = modulesList.filter(mod => PERMISSIONS[mod.id]?.includes(userRole));

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Segurança: se cair numa aba sem permissão (ex.: troca de perfil), volta ao dashboard.
    useEffect(() => {
        if (activeTab !== 'dashboard' && !PERMISSIONS[activeTab]?.includes(userRole)) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    // Verbete do Manual que abre expandido conforme a aba ativa.
    // (O botão "Manual da Tela" mostra TODO o módulo Fiscal; isto só escolhe o ponto de partida.)
    const getSecaoAjuda = () => {
        switch (activeTab) {
            case 'automacoes': return 'apuracao-impostos';
            case 'relatorios': return 'fechamento-fiscal';
            default:           return 'visao-geral-fiscal';
        }
    };

    // --- VISÃO GERAL (cards) ---
    const OverviewWidgets = () => (
        <div>
            {/* BANNER ROXO */}
            <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', borderRadius: '16px', padding: '40px', color: '#fff', marginBottom: '32px', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(124, 58, 237, 0.3)' }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>Fiscal</h2>
                    <p style={{ color: '#ede9fe', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>Ferramentas fiscais do grupo: apuração de impostos e auditoria do movimento fiscal (SPED × SAP/Sovos).</p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.15 }}>
                    <ShieldAlert size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Cada aba renderiza a ferramenta correspondente como conteúdo EMBUTIDO.
    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        switch (activeTab) {
            case 'automacoes': return <FiscalAutomacoes />;
            case 'relatorios': return <FiscalRelatorios />;
            default:           return <OverviewWidgets />;
        }
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                {/* CABEÇALHO — padrão dos módulos, na cor do Fiscal (roxo) */}
                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Fiscal"
                    cor="#7c3aed"
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: isMobile ? '20px' : '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* BARRA DE NAVEGAÇÃO (abas) */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px', backgroundColor: '#fff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0', width: '100%' }}>

                        <button onClick={() => setActiveTab('dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' : 'transparent', color: activeTab === 'dashboard' ? '#fff' : '#64748b', boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(124, 58, 237, 0.3)' : 'none', whiteSpace: 'nowrap' }}>
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ minWidth: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }}></div>

                        {allowedModules.map((mod) => (
                            <button key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent', backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent', color: activeTab === mod.id ? mod.color : '#64748b', whiteSpace: 'nowrap' }}>
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {renderContent()}
                </div>
            </div>

            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                secaoInicial={getSecaoAjuda()}
                modulo="fiscal"
            />
        </div>
    );
};

export default FiscalPainel;
